/**
 * Shared event/chart helpers used by the scoring engines (KP).
 *
 * Kept in their own module so lib/kp_scoring and lib/scoring can both use them without a
 * circular import.
 */

import { calculate_chart } from "../astro/chart";
import { get_config } from "../astro/config";
import type { RawChartData } from "../astro/models";
import type { PlainDateTime, PlainTime } from "./datetime";
import {
  addSeconds,
  combine,
  diffSeconds,
  formatISODateTime,
  parseISODate,
  tryParseISOTime,
} from "./datetime";
import { pythonRound } from "./pyutil";

// Event charts depend only on the event's datetime/place, never on the candidate birth
// time, so the same chart is reused across every scanned second. Caching it turns the
// per-second scan's dominant cost into a one-time computation per event.
const _EVENT_CHART_CACHE: Map<string, RawChartData> = new Map();

export function _cached_event_chart(
  event_dt: PlainDateTime,
  lat: number,
  lon: number,
  timezone_offset: number,
): RawChartData {
  const key = `${formatISODateTime(event_dt)}|${pythonRound(Number(lat) * 1_000_000)}|${pythonRound(
    Number(lon) * 1_000_000,
  )}|${pythonRound(Number(timezone_offset) * 10_000)}`;
  let chart = _EVENT_CHART_CACHE.get(key);
  if (chart === undefined) {
    chart = calculate_chart(event_dt, Number(lat), Number(lon), timezone_offset, get_config());
    _EVENT_CHART_CACHE.set(key, chart);
  }
  return chart;
}

/** Python `float(value)` returning null instead of raising; None/"" are null too. */
export function optional_float(value: any): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value === "boolean") {
    return value ? 1.0 : 0.0;
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const n = Number(value.trim());
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

/**
 * Parse an event clock time tolerant of both HH:MM and HH:MM:SS (the structured intake form
 * saves seconds; AI extraction may not). Returns None for empty/unparseable values rather than
 * raising, so a malformed time degrades to a date-only event instead of crashing the scan.
 */
export function _parse_event_clock(raw: any): PlainTime | null {
  const text = String(raw || "").trim();
  if (!text) {
    return null;
  }
  return tryParseISOTime(text);
}

/**
 * True when the event's DATE pins (effectively) a single day: the start→end span is ≤ 1 day AND
 * the stated date_accuracy_days is ≤ 1. Only then can an `event_time` clock be trusted as an
 * exact MOMENT — a clock riding on a multi-day range ("summer 2005, around 5 pm") pins nothing,
 * so it must not earn the exact-time weight, prana dasha depth, or a Ruling-Planet chart
 * (fix 2026-07-20; see _event_datetime_range).
 */
export function event_date_day_exact(payload: Record<string, any>): boolean {
  let span_days: number;
  try {
    const start_date = parseISODate(payload["start_date"]);
    const end_date = parseISODate(payload["end_date"] || payload["start_date"]);
    const midnight = { hour: 0, minute: 0, second: 0 };
    span_days = Math.abs(diffSeconds(combine(end_date, midnight), combine(start_date, midnight))) / 86400.0;
  } catch {
    return false;
  }
  const accuracy = optional_float(payload["date_accuracy_days"]) ?? 0.0;
  return span_days <= 1.0 && accuracy <= 1.0;
}

export function _event_datetime_range(
  payload: Record<string, any>,
): [PlainDateTime, PlainDateTime, boolean] {
  const start_date = parseISODate(payload["start_date"]);
  const end_date = parseISODate(payload["end_date"] || payload["start_date"]);
  const event_clock = _parse_event_clock(payload["event_time"]);
  // Exact-MOMENT status needs BOTH a parseable clock and a day-exact date: on a multi-day range
  // the clock stays ordinary info (kept in the range endpoints so the midpoint lands mid-range
  // at that clock) but the event scores as date-fuzzy, not as a timed anchor.
  const exact_moment = event_clock !== null && event_date_day_exact(payload);
  let start_dt = combine(start_date, event_clock ?? { hour: 0, minute: 0, second: 0 });
  let end_dt = combine(end_date, event_clock ?? { hour: 23, minute: 59, second: 0 });
  if (diffSeconds(end_dt, start_dt) < 0) {
    [start_dt, end_dt] = [end_dt, start_dt];
  }
  return [start_dt, end_dt, exact_moment];
}

export function event_midpoint_datetime(payload: Record<string, any>): PlainDateTime {
  const [start_dt, end_dt, has_exact_time] = _event_datetime_range(payload);
  return has_exact_time ? start_dt : addSeconds(start_dt, diffSeconds(end_dt, start_dt) / 2);
}

// How much MORE a clock-timed event counts than a merely day-exact one. 4.0 is a prior, not a
// calibrated value — lib/tuning sweeps it against clock-timed BenchmarkCases once verified event
// times exist (module-level so the harness can patch it; nothing else should write to it).
export let EXACT_TIME_WEIGHT_MULTIPLIER = 4.0;

/** Test/tuning seam mirroring Python's module-attribute patching of EXACT_TIME_WEIGHT_MULTIPLIER. */
export function set_exact_time_weight_multiplier(value: number): void {
  EXACT_TIME_WEIGHT_MULTIPLIER = value;
}

export function event_precision_weight_multiplier(
  date_accuracy_days: number,
  has_exact_time = false,
): number {
  if (has_exact_time) {
    return EXACT_TIME_WEIGHT_MULTIPLIER;
  }
  if (date_accuracy_days <= 0.75) {
    return 3.0;
  }
  if (date_accuracy_days <= 3) {
    return 2.0;
  }
  if (date_accuracy_days <= 7) {
    return 1.25;
  }
  if (date_accuracy_days <= 20) {
    return 0.6;
  }
  if (date_accuracy_days <= 190) {
    return 0.25;
  }
  return 0.1;
}
